import React from 'react';
import { RankingSnapshot } from '../services/api';
import {
  calculateActiveHoursSinceSale,
  calculateExpectedHours,
  formatTimeFromHours,
  getActiveCountingStart
} from '../utils/penaltyCalculations';
import { getActiveHoursLabel } from '../config/activeHours';

interface PenaltyBreakdownProps {
  ranking: RankingSnapshot;
  compact?: boolean;
}

interface PenaltyItem {
  type: 'penalty' | 'boost';
  label: string;
  value: number;
  icon: string;
  color: string;
}

const PenaltyBreakdown: React.FC<PenaltyBreakdownProps> = ({ ranking, compact = true }) => {
  // Extrair penalizações/boosts do campo notas
  const parsePenalties = (): PenaltyItem[] => {
    const items: PenaltyItem[] = [];
    const notas = ranking.notas || '';
    
    // Regex patterns para extrair informações
    const patterns = [
      { regex: /Sem vendas hoje \((-?\d+)%\)/, type: 'penalty', label: 'Sem vendas hoje', icon: 'bi-calendar-x' },
      { regex: /Sem vendas 2 dias \((-?\d+)%\)/, type: 'penalty', label: 'Sem vendas 2 dias', icon: 'bi-calendar2-x' },
      { regex: /Sem vendas 5\+ dias \((-?\d+)%\)/, type: 'penalty', label: 'Produto morto', icon: 'bi-x-octagon-fill' },
      { regex: /Declínio vendas \((-?\d+)%\)/, type: 'penalty', label: 'Declínio vendas', icon: 'bi-graph-down-arrow' },
      { regex: /Vendas em queda \((-?\d+)%\)/, type: 'penalty', label: 'Tendência negativa', icon: 'bi-trending-down' },
      { regex: /Recuperação \((\+?\d+)%\)/, type: 'boost', label: 'Recuperação', icon: 'bi-arrow-return-right' },
      { regex: /Preço ↓.*\(DPD:.*,\s*([+-]?\d+\.?\d*)%\)/, type: 'boost', label: 'Redução preço', icon: 'bi-tag' },
      { regex: /Preço ↑.*\(DPD:.*,\s*([+-]?\d+\.?\d*)%\)/, type: 'penalty', label: 'Aumento preço', icon: 'bi-tag-fill' }
    ];

    patterns.forEach(({ regex, type, label, icon }) => {
      const match = notas.match(regex);
      if (match) {
        const value = parseFloat(match[1]);
        items.push({
          type: type as 'penalty' | 'boost',
          label,
          value,
          icon,
          color: type === 'penalty' ? 'danger' : 'success'
        });
      }
    });

    return items;
  };

  const penalties = parsePenalties();
  const totalFactor = ranking.penalty_applied || 1;
  const totalPercentage = (totalFactor - 1) * 100;

  const formatPercentage = (value: number): string => {
    return `${value > 0 ? '+' : ''}${value.toFixed(0)}%`;
  };

  if (compact) {
    // Versão compacta para a tabela
    if (penalties.length === 0 && totalFactor === 1) {
      return (
        <div className="penalty-breakdown-compact text-muted small">
          <i className="bi bi-check-circle text-success me-1"></i>
          Sem ajustes
        </div>
      );
    }

    return (
      <div className="penalty-breakdown-compact">
        {penalties.slice(0, 2).map((item, index) => (
          <div key={index} className="small">
            <i className={`bi ${item.icon} text-${item.color} me-1`}></i>
            <span className={`text-${item.color}`}>{formatPercentage(item.value)}</span>
          </div>
        ))}
        {penalties.length > 2 && (
          <div className="small text-muted">+{penalties.length - 2} mais</div>
        )}
        <div className="small fw-bold mt-1">
          Total: <span className={totalPercentage >= 0 ? 'text-success' : 'text-danger'}>
            {formatPercentage(totalPercentage)}
          </span>
        </div>
      </div>
    );
  }

  // Formatar timestamp de última venda de forma legível
  // IMPORTANTE: Não calcular "há X horas/dias" pois isso usa hora do browser
  // A diferença de tempo correta está calculada abaixo usando calculation_timestamp
  const formatLastSale = (timestamp: string | null): string => {
    if (!timestamp) return 'Nunca vendeu';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-PT', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Versão completa
  return (
    <div className="penalty-breakdown-full">
      <h6 className="mb-3">Penalizações e Boosts Aplicados</h6>

      {/* Seção V2.0 - Sistema de Penalização Temporal */}
      {ranking.penalty_v2_category && ranking.penalty_v2_category !== 'v1_mode' && (
        <div className="mb-4 p-3 border rounded">
          <h6 className="mb-3">
            <i className="bi bi-clock-history text-primary me-2"></i>
            Sistema de Penalização V2.0 (Temporal)
          </h6>

          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <div className="small mb-2">
                <span className="text-muted">Fator de Penalização:</span>
                <div className="mt-1">
                  <span className="h5 mb-0">
                    {((ranking.penalty_v2_factor || 1) * 100).toFixed(0)}%
                  </span>
                  <span className={`badge ms-2 ${
                    (ranking.penalty_v2_factor || 1) >= 0.9 ? 'bg-success' :
                    (ranking.penalty_v2_factor || 1) >= 0.7 ? 'bg-warning' :
                    (ranking.penalty_v2_factor || 1) >= 0.5 ? 'bg-danger' :
                    'bg-dark'
                  }`}>
                    {formatPercentage(((ranking.penalty_v2_factor || 1) - 1) * 100)}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="small mb-2">
                <span className="text-muted">Categoria de Velocidade:</span>
                <div className="mt-1">
                  <span className={`badge bg-${
                    ranking.penalty_v2_category === 'ultra-fast' ? 'danger' :
                    ranking.penalty_v2_category === 'fast' ? 'warning' :
                    ranking.penalty_v2_category === 'medium' ? 'info' :
                    ranking.penalty_v2_category === 'slow' ? 'success' :
                    'secondary'
                  }`} style={{ fontSize: '0.9rem' }}>
                    {ranking.penalty_v2_category.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-12">
              <div className="small mb-2">
                <span className="text-muted">Última Venda:</span>
                <div className="mt-1">
                  <i className="bi bi-calendar-check me-1"></i>
                  <strong>{formatLastSale(ranking.last_sale_timestamp)}</strong>
                </div>
                {ranking.last_sale_timestamp && (() => {
                  const { isOutsideActiveHours, activeStartTime } = getActiveCountingStart(ranking.last_sale_timestamp);
                  return (
                    <>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {new Date(ranking.last_sale_timestamp).toLocaleString('pt-PT', {
                          timeZone: 'UTC',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {isOutsideActiveHours && (
                          <span className="badge bg-secondary ms-1" title={`Venda fora do horário ativo (${getActiveHoursLabel()})`}>
                            horário inativo
                          </span>
                        )}
                      </div>
                      {isOutsideActiveHours && activeStartTime && (
                        <div className="text-info mt-1" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-clock me-1"></i>
                          Contagem desde: {activeStartTime.toLocaleString('pt-PT', {
                            timeZone: 'UTC',
                            hour: '2-digit',
                            minute: '2-digit'
                          })} (início horário ativo)
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <div className="small">
                <span className="text-muted">Tempo desde última venda:</span>
                <div className="mt-1">
                  <i className="bi bi-clock-history me-1"></i>
                  <strong>
                    {ranking.last_sale_timestamp && ranking.calculation_timestamp
                      ? formatTimeFromHours(
                          calculateActiveHoursSinceSale(
                            ranking.last_sale_timestamp,
                            ranking.calculation_timestamp
                          )
                        )
                      : 'N/A'}
                  </strong>
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Horas ativas ({getActiveHoursLabel()})
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="small">
                <span className="text-muted">Expectativa:</span>
                <div className="mt-1">
                  <i className="bi bi-speedometer me-1"></i>
                  <strong>{formatTimeFromHours(calculateExpectedHours(ranking.dpd_7 || 0.01))}</strong>
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Baseado em DPD 7
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="small">
                <span className="text-muted">Atraso:</span>
                <div className="mt-1">
                  <i className="bi bi-hourglass-split me-1"></i>
                  <strong className={`text-${
                    (ranking.active_hours_overdue || 0) > calculateExpectedHours(ranking.dpd_7 || 0.01) * 0.5
                      ? 'danger'
                      : 'warning'
                  }`}>
                    {formatTimeFromHours(ranking.active_hours_overdue || 0)}
                  </strong>
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Acima da expectativa
                </div>
              </div>
            </div>
          </div>

          <div className="alert alert-info p-2 mb-0" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            <small>
              Penalização dinâmica baseada em horas ativas sem vendas.
              Produtos mais rápidos (DPD 7 alto) são penalizados mais agressivamente.
            </small>
          </div>
        </div>
      )}

      {penalties.length === 0 && totalFactor === 1 ? (
        <div className="alert alert-success p-2">
          <i className="bi bi-check-circle-fill me-2"></i>
          Nenhuma penalização ou boost aplicado
        </div>
      ) : (
        <>
          {/* Lista de penalizações/boosts */}
          <div className="mb-3">
            {penalties.map((item, index) => (
              <div key={index} className={`d-flex align-items-center mb-2 p-2 rounded bg-${item.color} bg-opacity-10`}>
                <i className={`bi ${item.icon} text-${item.color} me-2`} style={{ fontSize: '1.2rem' }}></i>
                <div className="flex-grow-1">
                  <span className="small">{item.label}</span>
                </div>
                <div>
                  <span className={`badge bg-${item.color}`}>
                    {formatPercentage(item.value)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Resumo do impacto total */}
          <div className="card bg-light">
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="small fw-bold">Fator Total Aplicado:</span>
                <span className={`h5 mb-0 ${totalPercentage >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatPercentage(totalPercentage)}
                </span>
              </div>
              <div className="small text-muted mt-1">
                Score Base × {totalFactor.toFixed(2)} = Score Final
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detalhes das mudanças de preço se houver */}
      {ranking.mudanca_preco_pct && ranking.mudanca_preco_pct !== 0 && (
        <div className="mt-3">
          <h6 className="small">Impacto da Mudança de Preço</h6>
          <div className="small text-muted">
            <div className="d-flex justify-content-between mb-1">
              <span>Preço anterior:</span>
              <span>€{(ranking.preco_anterior || 0).toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span>Preço atual:</span>
              <span>€{(ranking.preco_atual || 0).toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span>Mudança:</span>
              <span className={ranking.mudanca_preco_pct > 0 ? 'text-danger' : 'text-success'}>
                {formatPercentage(ranking.mudanca_preco_pct)}
              </span>
            </div>
            {ranking.impacto_previsto_dpd_pct && ranking.impacto_previsto_dpd_pct !== 0 && (
              <div className="d-flex justify-content-between">
                <span>Impacto DPD previsto:</span>
                <span className={ranking.impacto_previsto_dpd_pct > 0 ? 'text-success' : 'text-danger'}>
                  {formatPercentage(ranking.impacto_previsto_dpd_pct)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notas completas */}
      {ranking.notas && (
        <div className="mt-3">
          <h6 className="small">Notas Completas</h6>
          <div className="small text-muted p-2 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
            {ranking.notas}
          </div>
        </div>
      )}
    </div>
  );
};

export default PenaltyBreakdown;